import { useMemo, useState } from 'react';
import { BASE } from '../api/client';
import { useAssetLibrary } from '../hooks/useApi';

/**
 * Input híbrido para elegir un asset de imagen como referencia:
 *  - Tab "📤 Subir": file picker + upload directo a /projects/__character-gallery/assets.
 *  - Tab "📚 Existente": dropdown filtrable con assets image/* de la library.
 *  - Si hay assetId seleccionado: muestra preview thumb + botón quitar.
 *
 * Reemplaza al input de UUID a mano. Devuelve assetId via callback.
 */
export function AssetReferenceInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (assetId: string) => void;
  label?: string;
}) {
  const [tab, setTab] = useState<'upload' | 'pick'>('upload');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const library = useAssetLibrary({ q: search });
  const imageAssets = useMemo(
    () => (library.data ?? []).filter((a) => a.mimeType.startsWith('image/')).slice(0, 30),
    [library.data],
  );

  const onUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'reference');
      const res = await fetch(`${BASE}/projects/__character-gallery/assets`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload falló: HTTP ${res.status}`);
      const asset = (await res.json()) as { id: string };
      onChange(asset.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="text-xs text-slate-400 uppercase mb-1 block">{label}</label>}

      {/* Si ya hay asset seleccionado: preview compacto */}
      {value && (
        <div className="flex items-center gap-2 bg-slate-950 border border-vision/40 rounded p-2 mb-2">
          <img
            src={`${BASE}/assets/${value}/thumb`}
            alt=""
            className="w-12 h-12 object-cover rounded border border-slate-800"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <code className="text-[10px] text-slate-400 font-mono flex-1 truncate">{value}</code>
          <button
            onClick={() => onChange('')}
            className="text-xs text-slate-500 hover:text-red-400"
            title="Quitar referencia"
          >
            ✕
          </button>
        </div>
      )}

      {!value && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 mb-2">
            <button
              onClick={() => setTab('upload')}
              className={`px-3 py-1 text-xs rounded ${
                tab === 'upload'
                  ? 'bg-vision/20 text-vision border border-vision/40'
                  : 'bg-slate-800 text-slate-400 border border-slate-700'
              }`}
            >
              📤 Subir
            </button>
            <button
              onClick={() => setTab('pick')}
              className={`px-3 py-1 text-xs rounded ${
                tab === 'pick'
                  ? 'bg-vision/20 text-vision border border-vision/40'
                  : 'bg-slate-800 text-slate-400 border border-slate-700'
              }`}
            >
              📚 Existente ({imageAssets.length})
            </button>
          </div>

          {/* Upload */}
          {tab === 'upload' && (
            <div className="border border-dashed border-slate-700 rounded p-3 text-center">
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUpload(f);
                }}
                className="block w-full text-xs text-slate-300 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer"
              />
              {uploading && <div className="text-xs text-slate-500 mt-2">Subiendo…</div>}
            </div>
          )}

          {/* Pick existing */}
          {tab === 'pick' && (
            <div className="space-y-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar asset..."
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-vision"
              />
              {library.isLoading ? (
                <div className="text-xs text-slate-500 italic">Cargando…</div>
              ) : imageAssets.length === 0 ? (
                <div className="text-xs text-slate-500 italic">Sin assets de imagen.</div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-40 overflow-y-auto">
                  {imageAssets.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => onChange(a.id)}
                      className="bg-slate-950 border border-slate-800 hover:border-vision rounded p-1 transition-colors"
                      title={`${a.projectName ?? ''} · ${a.id.slice(0, 8)}`}
                    >
                      <img
                        src={`${BASE}/assets/${a.id}/thumb`}
                        alt=""
                        className="w-full aspect-square object-cover rounded"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 mt-2">
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
